import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import agent from "../../utils/axios";

export interface QuickBooksCompany {
  name: string;
  realmId: string;
  hasValidToken: boolean;
  accessTokenExpired: boolean;
}

interface CompanySelectDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (company: QuickBooksCompany) => void;
}

/**
 * Lets the user pick which QuickBooks company (RealmId) a bill should be sent to before
 * Convert to Check. Mirrors the COMPASS company-selection pattern: the list comes from the
 * backend (per-environment config), and we show whether each company already has a valid
 * token so the caller can skip the OAuth popup.
 */
function CompanySelectDialog({
  open,
  onClose,
  onConfirm,
}: CompanySelectDialogProps) {
  const [companies, setCompanies] = useState<QuickBooksCompany[]>([]);
  // Track the selection by row INDEX, not realmId. Lower environments map every company to
  // the same sandbox realmId, so using realmId as the option value makes the entries
  // indistinguishable to MUI Select (the selection won't stick). Index is always unique.
  const [selectedIndex, setSelectedIndex] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    let active = true;
    async function loadCompanies() {
      setLoading(true);
      setError(null);
      try {
        const result: QuickBooksCompany[] =
          await agent.quickbooks.getCompanies();
        if (!active) return;
        setCompanies(result);
        // Auto-select when only one company is configured.
        if (result.length === 1) {
          setSelectedIndex("0");
        }
      } catch (e) {
        if (active) setError("Unable to load QuickBooks companies.");
      } finally {
        if (active) setLoading(false);
      }
    }

    loadCompanies();
    return () => {
      active = false;
    };
  }, [open]);

  const handleConfirm = () => {
    const company = companies[Number(selectedIndex)];
    if (company) onConfirm(company);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Select QuickBooks Company</DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography variant="body2">Loading companies…</Typography>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : companies.length === 0 ? (
          <Typography variant="body2">
            No QuickBooks companies are configured.
          </Typography>
        ) : (
          <FormControl fullWidth size="small" sx={{ mt: 1 }}>
            <InputLabel id="qb-company-label">QuickBooks Company *</InputLabel>
            <Select
              labelId="qb-company-label"
              label="QuickBooks Company *"
              value={selectedIndex}
              onChange={(e) => setSelectedIndex(e.target.value)}
            >
              {companies.map((company, index) => (
                <MenuItem
                  key={`${company.realmId}-${index}`}
                  value={String(index)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <span>{company.name}</span>
                    {!company.hasValidToken && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: "auto" }}
                      >
                        (sign-in required)
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || selectedIndex === ""}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CompanySelectDialog;
